import { extendTheme } from "@chakra-ui/react";

const theme = extendTheme({
  fonts: {
    heading: "Georgia, 'Times New Roman', serif",
    body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  styles: {
    global: {
      body: {
        bg: "surface.canvas",
        color: "gray.800",
      },
      "*::placeholder": {
        color: "text.muted",
      },
    },
  },
  colors: {
    // Bordeaux — the editorial accent used across the atlas pages, now the
    // brand accent site-wide (kept under the "gwbi" key so existing usages adopt it).
    gwbi: {
      50: "#f8edec",
      100: "#edd0ce",
      200: "#ddaba8",
      300: "#ca8581",
      400: "#b25f5a",
      500: "#974440",
      600: "#7a2e2e",
      700: "#5f2323",
      800: "#411717",
      900: "#250c0c",
    },
    gwbiBlue: {
      50: "#e7f0fb",
      100: "#c5dbf4",
      200: "#9fc4ec",
      300: "#77ace3",
      400: "#5095da",
      500: "#347cc0",
      600: "#28639a",
      700: "#1c4870",
      800: "#102f48",
      900: "#061823",
    },
  },
  semanticTokens: {
    colors: {
      "surface.canvas": { default: "#fbfaf8", _dark: "gray.900" },
      "surface.card": { default: "white", _dark: "gray.800" },
      "surface.subtle": { default: "#f4efe8", _dark: "gray.700" },
      "surface.muted": { default: "#efe9df", _dark: "gray.700" },
      "surface.rail": { default: "#f3eee6", _dark: "gray.800" },
      "border.default": { default: "#e7dfd2", _dark: "gray.700" },
      "text.muted": { default: "gray.600", _dark: "gray.400" },
      "icon.theme.bg": { default: "gwbi.50", _dark: "gwbi.900" },
      "icon.theme.fg": { default: "gwbi.700", _dark: "gwbi.200" },
      "icon.shared.bg": { default: "gwbi.50", _dark: "gwbi.900" },
      "icon.shared.fg": { default: "gwbi.700", _dark: "gwbi.200" },
      "icon.neutral.bg": { default: "gray.100", _dark: "gray.600" },
      "icon.neutral.fg": { default: "gray.700", _dark: "gray.200" },
    },
  },
  space: {
    "ui-1": "0.25rem",
    "ui-2": "0.5rem",
    "ui-3": "0.75rem",
    "ui-4": "1rem",
    "ui-5": "1.25rem",
    "ui-6": "1.5rem",
    "ui-8": "2rem",
    "ui-10": "2.5rem",
  },
  radii: {
    "ui-sm": "0.5rem",
    "ui-md": "0.75rem",
    "ui-lg": "1rem",
    "ui-xl": "1.5rem",
    pill: "9999px",
  },
  shadows: {
    "ui-sm": "0 6px 14px rgba(0, 0, 0, 0.08)",
    "ui-md": "0 12px 28px rgba(0, 0, 0, 0.12)",
  },
  fontSizes: {
    "ui-xs": "0.75rem",
    "ui-sm": "0.875rem",
    "ui-md": "1rem",
    "ui-lg": "1.125rem",
    "ui-xl": "1.25rem",
  },
  fontWeights: {
    "ui-medium": 500,
    "ui-semibold": 600,
  },
  letterSpacings: {
    "ui-wide": "0.08em",
  },
  textStyles: {
    sectionTitle: {
      fontSize: "sm",
      fontWeight: "ui-semibold",
      letterSpacing: "ui-wide",
      textTransform: "uppercase",
    },
    eyebrow: {
      fontSize: "xs",
      letterSpacing: "ui-wide",
      textTransform: "uppercase",
    },
    helper: {
      fontSize: "sm",
      color: "text.muted",
    },
    // Serif title used for cards and rail sections, matching the landing/atlas.
    cardTitle: {
      fontFamily: "heading",
      fontWeight: "bold",
      fontSize: "md",
      lineHeight: "1.25",
    },
  },
  components: {
    Button: {
      baseStyle: {
        borderRadius: "ui-md",
        fontWeight: "ui-semibold",
      },
    },
    Card: {
      baseStyle: {
        container: {
          bg: "surface.card",
          borderColor: "border.default",
          borderWidth: "1px",
          borderRadius: "ui-lg",
          boxShadow: "ui-sm",
        },
      },
    },
    Badge: {
      baseStyle: {
        borderRadius: "pill",
        textTransform: "none",
        fontWeight: "ui-medium",
      },
    },
    Input: {
      variants: {
        outline: {
          field: {
            bg: "surface.card",
            borderColor: "border.default",
            _hover: { borderColor: "gray.300" },
            _focus: {
              borderColor: "gwbi.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-gwbi-500)",
            },
          },
        },
      },
    },
    Textarea: {
      variants: {
        outline: {
          bg: "surface.card",
          borderColor: "border.default",
          _hover: { borderColor: "gray.300" },
          _focus: {
            borderColor: "gwbi.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-gwbi-500)",
          },
        },
      },
    },
    Select: {
      variants: {
        outline: {
          field: {
            bg: "surface.card",
            borderColor: "border.default",
            _hover: { borderColor: "gray.300" },
            _focus: {
              borderColor: "gwbi.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-gwbi-500)",
            },
          },
        },
      },
    },
    Tabs: {
      baseStyle: {
        tab: {
          fontWeight: "ui-medium",
          _selected: {
            color: "gray.900",
          },
        },
      },
    },
  },
});

export default theme;
